import { useCallback, useMemo } from 'react';
import { useActivePackages } from './useActivePackages';
import { PLATFORM_ACCESS_UUID } from './useDowngradeOptions';

const WEEKS_OPTIONS = [4, 12, 24, 52];
const MAX_WEEKS = Math.max(...WEEKS_OPTIONS);

export interface UpgradePackageOption {
  value: string;
  label: string;
  sessionsPerWeek: number;
}

export interface UseUpgradeOptionsParams {
  /** UUID of the plan the student is currently enrolled to. */
  student_plan_uuid: string;
  /** UUID of the plan currently selected in the dropdown. */
  selectedPackageUUID: string;
  no_of_weeks_originally_enrolled_to: number;
}

// Selectable upgrade targets: plans at or above the current tier (the inverse of
// downgrade). Platform Access members (the base tier, unknown in the 1-on-1 catalog) get
// every plan as an upgrade; any other unresolved current plan offers nothing (see the
// currentTier === -1 handling below). Platform Access itself is a downgrade-only target and
// is never offered here. When the student is already at the maximum cadence (52 weeks) on
// their exact current plan, that plan is dropped because there are no extra weeks to add.
export const buildUpgradePlanOptions = ({
  allPackages,
  currentTier,
  student_plan_uuid,
  no_of_weeks_originally_enrolled_to
}: {
  allPackages: UpgradePackageOption[];
  currentTier: number;
  student_plan_uuid: string;
  no_of_weeks_originally_enrolled_to: number;
}): UpgradePackageOption[] => {
  const atMaxWeeks = Number(no_of_weeks_originally_enrolled_to || 0) >= MAX_WEEKS;
  // currentTier === -1 means the current plan doesn't resolve to a known 1-on-1 tier:
  // either the base Platform Access plan, or a retired/undefined plan absent from the
  // catalog. Preserve the pre-API behavior (old hardcoded flow returned the full list only
  // for Platform Access and [] otherwise): the base plan makes every plan an upgrade, but
  // any *other* unresolved plan offers nothing — never the whole catalog, which would
  // surface downgrades/group classes as bogus "upgrades".
  const currentIsPlatformAccess = student_plan_uuid === PLATFORM_ACCESS_UUID;
  return allPackages.filter((pkg) => {
    if (pkg.value === PLATFORM_ACCESS_UUID) return false;
    if (atMaxWeeks && pkg.value === student_plan_uuid) return false;
    if (currentTier === -1) return currentIsPlatformAccess;
    return pkg.sessionsPerWeek >= currentTier;
  });
};

// Allowed billing-cadence (weeks) options: switching to a different plan offers the full
// cadence list; staying on the same plan must add weeks (strictly greater than the current
// enrollment), falling back to the full list if none are greater.
export const buildUpgradeWeeksOptions = ({
  selectedPackageUUID,
  student_plan_uuid,
  no_of_weeks_originally_enrolled_to
}: {
  selectedPackageUUID: string;
  student_plan_uuid: string;
  no_of_weeks_originally_enrolled_to: number;
}): number[] => {
  if (!selectedPackageUUID || selectedPackageUUID !== student_plan_uuid) {
    return WEEKS_OPTIONS;
  }
  const currentWeeks = Number(no_of_weeks_originally_enrolled_to || 0);
  const filtered = WEEKS_OPTIONS.filter((w) => w > currentWeeks);
  return filtered.length ? filtered : WEEKS_OPTIONS;
};

/**
 * Single source of truth for the membership-upgrade option lists in UpgradeMembership.
 * Mirrors useDowngradeOptions: plan tier is derived from the package's `sessions_per_week`
 * (from the active_packages API) rather than a hardcoded UUID list, so newer plans —
 * including the Consistency 1/2/3-per-week tiers and the hidden 8-21 tiers — appear
 * automatically. Upgrade and downgrade now share the same catalog and can never drift apart.
 */
export const useUpgradeOptions = ({
  student_plan_uuid,
  selectedPackageUUID,
  no_of_weeks_originally_enrolled_to
}: UseUpgradeOptionsParams) => {
  const { packages, error } = useActivePackages();

  const allPackages = useMemo<UpgradePackageOption[]>(
    () =>
      packages.map((p) => ({
        value: p.uuid,
        label: p.name,
        sessionsPerWeek: p.sessions_per_week
      })),
    [packages]
  );

  const getPlanTier = useCallback(
    (uuid: string) => {
      const pkg = allPackages.find((p) => p.value === uuid);
      return pkg ? pkg.sessionsPerWeek : -1;
    },
    [allPackages]
  );

  const currentTier = getPlanTier(student_plan_uuid);
  const selectedTier = getPlanTier(selectedPackageUUID);

  const planOptions = useMemo(
    () =>
      buildUpgradePlanOptions({
        allPackages,
        currentTier,
        student_plan_uuid,
        no_of_weeks_originally_enrolled_to
      }),
    [allPackages, currentTier, student_plan_uuid, no_of_weeks_originally_enrolled_to]
  );

  const weeksOptions = useMemo(
    () =>
      buildUpgradeWeeksOptions({
        selectedPackageUUID,
        student_plan_uuid,
        no_of_weeks_originally_enrolled_to
      }),
    [selectedPackageUUID, student_plan_uuid, no_of_weeks_originally_enrolled_to]
  );

  const getPackageName = useCallback(
    (uuid: string) => allPackages.find((p) => p.value === uuid)?.label || uuid,
    [allPackages]
  );

  return {
    allPackages,
    planOptions,
    weeksOptions,
    currentTier,
    selectedTier,
    getPackageName,
    error
  };
};
